import QuickFilter from "../../../widgets/QuickFilter";
import ImageNotSupportedIcon from "@mui/icons-material/ImageNotSupported";
import InfoIcon from "@mui/icons-material/Info";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CardHeader from "@mui/material/CardHeader";
import CardMedia from "@mui/material/CardMedia";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import ThumbnailGrid from "../../../widgets/ThumbnailGrid";
import Pagination from "@mui/material/Pagination";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { useEffect, useRef, useState } from "react";

import { usePricingEstimatorContext } from "..";
import { backendURL } from "../../../../constants";
import InfoRow from "../../../widgets/InfoRow";
import { formatPrice, formatPricePerCarat } from "../../../../tools";
import { GetPricingEstimationMutationData } from "../graphql";

const IMAGE_WIDTH = 196;
const IMAGE_HEIGHT = 128;
const IMAGE_GAP = 8;

type SimilarDiamond =
  GetPricingEstimationMutationData["estimateDiamondPrice"]["similarDiamonds"][0];

type SubmittedFormValues = NonNullable<
  ReturnType<typeof usePricingEstimatorContext>["submittedFormValuesState"][0]
>;

type FilterPredicate = (diamond: SimilarDiamond) => boolean;

type Filters = {
  source?: FilterPredicate;
  gIAColor?: FilterPredicate;
  langermanColor?: FilterPredicate;
  flatColor?: FilterPredicate;
  meleeColor?: FilterPredicate;
  clarity?: FilterPredicate;
};

function DiamondTooltip({ diamond }: { diamond: SimilarDiamond }) {
  return (
    <Stack direction="column" spacing={1}>
      {/* Diamond Specifications */}
      <Box>
        <Typography variant="overline">Characteristics</Typography>
        <Stack>
          <InfoRow label="Source" value={diamond.source} />
          <InfoRow label="GIA Color" value={diamond.color} />
          <InfoRow label="Langerman Color" value={diamond.langermanColor} />
          <InfoRow label="Flat" value={diamond.flat} />
          <InfoRow label="Melee" value={diamond.melee} />
          <InfoRow label="Clarity" value={diamond.clarity} />
          <InfoRow label="Carat" value={diamond.carat} />
          <InfoRow label="Tags" value={diamond.tags} />
        </Stack>
      </Box>

      {/* Pricing Information */}
      <Box>
        <Typography variant="overline">Pricing</Typography>
        <Stack>
          <InfoRow label="Price" value={formatPrice(diamond.price)} />
          <InfoRow
            label="Price/Carat"
            value={formatPricePerCarat(diamond.perCarat)}
          />
          <InfoRow
            label="Normalized Price/Carat"
            value={formatPricePerCarat(diamond.perCaratNormalized)}
          />
        </Stack>
      </Box>
    </Stack>
  );
}

function SimilarDiamondCard({
  diamond,
  submittedFactors,
  displayNormalizedPrice,
}: {
  diamond: SimilarDiamond;
  submittedFactors: SubmittedFormValues["factors"];
  displayNormalizedPrice: boolean;
}) {
  const diamondTags = diamond.tags?.split(",").map((tag) => tag.trim()) ?? [];
  const commonFactors = submittedFactors
    .filter((factor) => {
      return (
        (factor.sheetType === "SourceFactor" &&
          factor.label === diamond.source) ||
        (factor.sheetType === "TagFactor" && diamondTags.includes(factor.label))
      );
    })
    .map((factor) => ({
      key: factor.id,
      label: factor.label,
    }));

  return (
    <Tooltip
      key={diamond.sKU}
      title={<DiamondTooltip diamond={diamond} />}
      placement="top"
      followCursor
    >
      <Card variant="outlined" sx={{ width: IMAGE_WIDTH, height: "100%" }}>
        <CardHeader
          sx={{ paddingY: 1 }}
          titleTypographyProps={{ variant: "overline" }}
          title={`SKU ${diamond.sKU}`}
          action={
            <IconButton
              size="small"
              onClick={() =>
                window.open("/analytics/diamondpricing/" + diamond.iD, "_blank")
              }
            >
              <OpenInNewIcon />
            </IconButton>
          }
        />
        {diamond.representationImageID ? (
          <CardMedia
            component="img"
            sx={{
              width: IMAGE_WIDTH,
              height: IMAGE_HEIGHT,
              objectFit: "contain",
            }}
            image={`${backendURL}/image/thumbnail/${diamond.representationImageID}`}
          />
        ) : (
          <Box
            sx={{
              width: IMAGE_WIDTH,
              height: IMAGE_HEIGHT,
              backgroundColor: "#00000010",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <ImageNotSupportedIcon sx={{ fontSize: IMAGE_HEIGHT / 4 }} />
            <span>No Image</span>
          </Box>
        )}
        <CardContent>
          <Typography variant="overline">
            {formatPricePerCarat(
              displayNormalizedPrice
                ? diamond.perCaratNormalized
                : diamond.perCarat,
            )}
          </Typography>

          <Stack direction="row" alignItems="center">
            <Typography variant="subtitle2">
              {`${diamond.matchingScore.toFixed(0)}% similar`}
            </Typography>
            <IconButton
              size="small"
              onClick={() =>
                window.open(
                  "https://www.notion.so/langerman/Diamond-Similarity-Measure-2e90574f353580f1b413ede4c5383242?source=copy_link",
                  "_blank",
                )
              }
            >
              <InfoIcon fontSize="small" />
            </IconButton>
          </Stack>

          <Box
            sx={{
              marginTop: 1,
              display: "flex",
              flexDirection: "row",
              flexWrap: "wrap",
              gap: 0.5,
            }}
          >
            {commonFactors.map((factor) => (
              <Chip key={factor.key} label={factor.label} size="small" />
            ))}
          </Box>
        </CardContent>
      </Card>
    </Tooltip>
  );
}

export function SimilarDiamondsSkeleton() {
  return (
    <Stack direction="column" spacing={2}>
      <Skeleton variant="rounded" height={32} />

      <Stack direction="column" spacing={IMAGE_GAP / 8}>
        <Skeleton
          variant="rounded"
          width={"100%"}
          height={IMAGE_HEIGHT + 160}
        />
        <Skeleton
          variant="rounded"
          width={"100%"}
          height={IMAGE_HEIGHT + 160}
        />
      </Stack>

      <Skeleton
        sx={{ alignSelf: "center" }}
        variant="rounded"
        width={320}
        height={32}
      />
    </Stack>
  );
}

export function SimilarDiamonds() {
  const ref = useRef(null);
  const [width, setWidth] = useState(0);

  const {
    displayNormalizedPricesState,
    mutationResult,
    submittedFormValuesState,
  } = usePricingEstimatorContext();

  const diamonds =
    mutationResult.data?.estimateDiamondPrice?.similarDiamonds ?? [];
  const displayNormalizedPrice = displayNormalizedPricesState[0];
  const submittedFormValues = submittedFormValuesState[0];

  const [filters, setFilters] = useState<Filters>({});

  const filteredDiamonds = diamonds.filter((diamond) =>
    Object.values(filters).every((predicate) => predicate(diamond)),
  );

  const cols = Math.max(
    1,
    Math.floor((width + IMAGE_GAP) / (IMAGE_WIDTH + IMAGE_GAP)),
  );
  const rows = 2;
  const imagesPerPage = cols * rows;
  const nPages = Math.ceil(filteredDiamonds.length / imagesPerPage);
  const [page, setPage] = useState(1);

  const makeToggleFilter =
    (field: keyof Filters, predicate: FilterPredicate) => (active: boolean) => {
      if (active) {
        const { [field]: _, ...otherFilters } = filters;
        setFilters(otherFilters);
      } else {
        setFilters({ ...filters, [field]: predicate });
      }
    };

  // This resize observer makes sure that the number of columns in the image grid updates when the page is resized
  useEffect(() => {
    if (ref.current) {
      const observer = new ResizeObserver((entries) => {
        if (entries.length !== 1) {
          return;
        }
        setWidth(entries[0].contentRect.width);
      });

      observer.observe(ref.current);

      return () => {
        observer.disconnect();
      };
    }
  }, []);

  // when filters change, reset to page 1 to avoid being on a page number that doesn't exist anymore
  useEffect(() => {
    setPage(1);
  }, [filters]);

  // after a filter or window resizing, make sure the current page number is not larger than the total number of pages
  useEffect(() => {
    if (page > nPages) {
      setPage(nPages);
    }
  }, [page, nPages]);

  return (
    <Stack direction="column" spacing={2} ref={ref} sx={{ width: "100%" }}>
      <Box
        sx={{
          width,
          display: "flex",
          flexDirection: "row",
          gap: 1,
          flexWrap: "wrap",
        }}
      >
        <QuickFilter
          label="Langerman only"
          active={!!filters.source}
          toggle={makeToggleFilter("source", (d) => d.source === "Langerman")}
        />
        {!!submittedFormValues?.gIAColor && (
          <QuickFilter
            label={`${submittedFormValues?.gIAColor} only`}
            active={!!filters.gIAColor}
            toggle={makeToggleFilter(
              "gIAColor",
              (d) => d.color === submittedFormValues?.gIAColor,
            )}
          />
        )}
        {!!submittedFormValues?.langermanColor && (
          <QuickFilter
            label={`${submittedFormValues?.langermanColor} only`}
            active={!!filters.langermanColor}
            toggle={makeToggleFilter(
              "langermanColor",
              (d) => d.langermanColor === submittedFormValues?.langermanColor,
            )}
          />
        )}
        {!!submittedFormValues?.flatColor && (
          <QuickFilter
            label={`Flat ${submittedFormValues?.flatColor} only`}
            active={!!filters.flatColor}
            toggle={makeToggleFilter(
              "flatColor",
              (d) => `${d.flat}` === submittedFormValues?.flatColor,
            )}
          />
        )}
        {!!submittedFormValues?.meleeColor && (
          <QuickFilter
            label={`Melee ${submittedFormValues?.meleeColor} only`}
            active={!!filters.meleeColor}
            toggle={makeToggleFilter(
              "meleeColor",
              (d) => `${d.melee}` === submittedFormValues?.meleeColor,
            )}
          />
        )}
        <QuickFilter
          label={`Clarity ${submittedFormValues?.clarity} only`}
          active={!!filters.clarity}
          toggle={makeToggleFilter(
            "clarity",
            (d) => d.clarity === submittedFormValues?.clarity,
          )}
        />
      </Box>

      {filteredDiamonds.length > 0 ? (
        <>
          <ThumbnailGrid minWidth={IMAGE_WIDTH} gap={IMAGE_GAP / 8}>
            {filteredDiamonds
              .slice((page - 1) * imagesPerPage, page * imagesPerPage)
              .map((diamond) => (
                <SimilarDiamondCard
                  key={diamond.iD}
                  diamond={diamond}
                  submittedFactors={submittedFormValues?.factors ?? []}
                  displayNormalizedPrice={displayNormalizedPrice}
                />
              ))}
          </ThumbnailGrid>

          <Pagination
            sx={{ alignSelf: "center" }}
            count={nPages}
            page={page}
            onChange={(_, value) => setPage(value)}
          />
        </>
      ) : (
        <Typography variant="body2">No diamonds match the criteria</Typography>
      )}
    </Stack>
  );
}
